import {
  airHelp,
  allInclusiveProtection,
  baggageService,
  bankruptcyInsuranceCoverGenius,
  cabinBaggage,
  cancelForAnyReason,
  cancellationGuarantee,
  cancellationInsideEu,
  cancellationInsuranceCoverGenius,
  cancellationOutsideEu,
  checkInBaggage,
  climateCompensation,
  comprehensiveInsuranceCoverGenius,
  cancelTwentyFourHours,
  co2Compensation,
  fastTrack,
  flexibleTicket,
  meal,
  mobileTravelPlan,
  onlineCheckIn,
  platinum,
  qPass,
  seatMap,
  simpleVisa,
  sms,
  travelDocsPost,
  travelInsuranceCoverGenius,
  travelInsuranceInsideEu,
  travelInsuranceOutsideEu,
  tripCancellationProtection,
} from '@eti/products';

const newProduct = (
  id,
  name,
  title,
  numberOfUnits,
  price,
  markup = null,
  travelers = [],
  availability = null,
) => ({
  id,
  name,
  title,
  numberOfUnits,
  price,
  markup,
  travelers,
  availability,
});

const mapProductsToTravelers = (selected) => {
  const travelerProducts = [];
  if (selected?.length > 0) {
    selected.forEach(({ travelerId, id, value, priceRaw, ...rest }) => {
      if (travelerId) {
        travelerProducts.push({ travelerId, priceRaw, ...rest });
      } else if (id) {
        travelerProducts.push({ travelerId: id, priceRaw, ...rest });
      } else if (value !== 'NONE') {
        travelerProducts.push({ travelerId: value, priceRaw, ...rest });
      }
      return travelerProducts;
    });
  }
  return travelerProducts;
};

export const updateCarrierPriceBreakdowns = (carrierPriceBreakdowns = [], currentCarrierPrice) => {
  const matchingBreakdown = carrierPriceBreakdowns.find(
    (breakdown) => breakdown?.carrier?.code === currentCarrierPrice?.carrier?.code,
  );

  if (matchingBreakdown) {
    return carrierPriceBreakdowns.map((priceBreakdown) =>
      priceBreakdown?.carrier?.code === currentCarrierPrice?.carrier?.code
        ? {
            ...priceBreakdown,
            providerPrice: {
              value: priceBreakdown.providerPrice.value + currentCarrierPrice.providerPrice.value,
            },
          }
        : priceBreakdown,
    );
  }

  return [...carrierPriceBreakdowns, currentCarrierPrice];
};

export const mapSeatMapProductToTraveler = (selected, productId) => {
  if (!selected?.length) {
    return [];
  }

  return selected
    ?.flatMap((seatPriceDetails) => Object.values(seatPriceDetails))
    ?.reduce((acc, current) => {
      const foundItemIndex = acc?.findIndex(
        (item) => String(item?.travelerId) === String(current.id),
      );

      const currentCarrierPrice = {
        carrier: current?.carrier,
        providerPrice: { value: (current?.price ?? 0) - (current?.markup ?? 0) },
      };

      if (foundItemIndex !== -1) {
        const existingTraveler = acc[foundItemIndex];

        acc[foundItemIndex] = {
          ...existingTraveler,
          ancillaryCarrierPriceBreakdowns: updateCarrierPriceBreakdowns(
            existingTraveler?.ancillaryCarrierPriceBreakdowns,
            currentCarrierPrice,
          ),
          id: productId,
          priceRaw: (existingTraveler?.priceRaw ?? 0) + (current?.price ?? 0),
          markup: (existingTraveler?.markup ?? 0) + (current?.markup ?? 0),
        };
      } else {
        acc.push({
          ancillaryCarrierPriceBreakdowns: [currentCarrierPrice],
          id: productId,
          travelerId: String(current.id),
          priceRaw: current?.price ?? 0,
          markup: current?.markup ?? 0,
        });
      }
      return acc;
    }, []);
};

export const selectionContainsNoneOption = (selection) =>
  Boolean(selection && selection.some((option) => option.value === 'NONE'));

const getNumberOfUnitsProducts =
  (form, names = {}) =>
  (state) =>
    Object.entries({
      checkInBaggage,
      meal,
      flexibleTicket,
      cabinBaggage,
    }).reduce((acc, [name, product]) => {
      const numberOfUnits = product.getNumberOfUnits(form)(state);
      const selected = product.getSelected(form)(state);
      const hasNoneAsSelection = selectionContainsNoneOption(selected);
      const hasNoSelection = !(numberOfUnits > 0 && selected?.length > 0);
      const travelers = mapProductsToTravelers(selected);

      return hasNoneAsSelection || hasNoSelection
        ? acc
        : [
            ...acc,
            newProduct(
              product.id,
              name,
              names[name],
              numberOfUnits,
              product.getPrice(form)(state),
              product.getMarkup && product.getMarkup(form)(state),
              travelers,
            ),
          ];
    }, []);

export const getSpecialChoiceProducts =
  (form, names = {}) =>
  (state) =>
    Object.entries({
      tripCancellationProtection,
      allInclusiveProtection,
      simpleVisa,
    }).reduce((arr, [name, product]) => {
      const isSelected = product.getSelected(form)(state);
      const price = product.getPrice(form)(state);

      if (isSelected && price) {
        arr.push(newProduct(product.id, name, names[name], undefined, price));
      }

      return arr;
    }, []);

const getFastTrack =
  (form, names = {}) =>
  (state) =>
    Object.entries({ fastTrack }).reduce((arr, [name, product]) => {
      const isSelected = product.getSelected(form)(state);

      if (isSelected) {
        const availability = product.getAvailability(form)(state);

        arr.push(
          newProduct(
            product.id,
            name,
            names[name],
            undefined,
            product.getPrice(form)(state),
            null,
            [],
            availability,
          ),
        );
      }

      return arr;
    }, []);

const getChoiceProducts =
  (form, names = {}) =>
  (state) =>
    Object.entries({
      airHelp,
      baggageService,
      bankruptcyInsuranceCoverGenius,
      cancelForAnyReason,
      cancellationGuarantee,
      cancellationInsideEu,
      cancellationInsuranceCoverGenius,
      cancellationOutsideEu,
      climateCompensation,
      comprehensiveInsuranceCoverGenius,
      cancelTwentyFourHours,
      co2Compensation,
      mobileTravelPlan,
      onlineCheckIn,
      platinum,
      qPass,
      sms,
      travelDocsPost,
      travelInsuranceCoverGenius,
      travelInsuranceInsideEu,
      travelInsuranceOutsideEu,
    }).reduce((arr, [name, product]) => {
      const isSelected = product.getSelected(form)(state);

      if (isSelected) {
        arr.push(
          newProduct(product.id, name, names[name], undefined, product.getPrice(form)(state)),
        );
      }

      return arr;
    }, []);

const getSeatMapProducts =
  (productType = seatMap, form, names = {}) =>
  (state) =>
    Object.entries({ productType }).reduce((arr, [, product]) => {
      const selected = product.isSelected(form)(state);
      const selection = product.getSelection(form)(state) || [];
      const travelers = mapSeatMapProductToTraveler(selection, product.id);

      if (selected && Array.isArray(selection) && selection.length > 0) {
        arr.push(
          newProduct(
            product.id,
            product.name,
            names[product.name],
            undefined,
            product.getPrice(form)(state),
            product.getMarkup(form)(state),
            travelers,
          ),
        );
      }

      return arr;
    }, []);

export const getAddedExtraProducts =
  (form, names = {}) =>
  (state) => [
    ...getChoiceProducts(form, names)(state),
    ...getNumberOfUnitsProducts(form, names)(state),
    ...getSeatMapProducts(seatMap, form, names)(state),
    ...getSpecialChoiceProducts(form, names)(state),
    ...getFastTrack(form, names)(state),
  ];

export const getExtraProductsTotalPrice = (form) => (state) =>
  [
    airHelp,
    allInclusiveProtection,
    baggageService,
    bankruptcyInsuranceCoverGenius,
    cancellationInsuranceCoverGenius,
    cabinBaggage,
    cancelForAnyReason,
    cancellationGuarantee,
    cancellationInsideEu,
    cancellationOutsideEu,
    checkInBaggage,
    climateCompensation,
    comprehensiveInsuranceCoverGenius,
    cancelTwentyFourHours,
    co2Compensation,
    fastTrack,
    flexibleTicket,
    meal,
    mobileTravelPlan,
    onlineCheckIn,
    platinum,
    seatMap,
    simpleVisa,
    sms,
    travelDocsPost,
    travelInsuranceCoverGenius,
    travelInsuranceInsideEu,
    travelInsuranceOutsideEu,
    tripCancellationProtection,
  ].reduce((total, product) => {
    const isSeatMapProduct = product.id === seatMap.id;
    const selected = (isSeatMapProduct ? product.getSelection : product.getSelected)(form)(state);

    if ((Array.isArray(selected) && selected.length === 0) || !selected) {
      return total;
    }
    return total + (product.getPrice(form)(state) || 0);
  }, 0);

export const isSeatingOffered = (products) => {
  if (products.length > 0 && Array.isArray(products[0])) {
    const flattenedProducts = products.flat();
    return flattenedProducts.length > 0
      ? flattenedProducts.filter((product) => product.id === seatMap.id)
      : false;
  }
  return products.length > 0 ? products.filter((product) => product.id === seatMap.id) : false;
};
